/**
 * OutdoorWindows — recommends 1-3 "outdoor windows" today based on prayer
 * times + weather, with a "Add to Yawmi" CTA per window. Tier 2 #17.
 *
 * Render flow:
 *   1. mount() emits the skeleton synchronously.
 *   2. After first paint, fetches WeatherDetails (already cached SWR-style
 *      by WeatherService) and runs PrayerWeatherIntelligence.computeOutdoorIntel.
 *   3. Replaces skeleton with the cards. Hydration nudge sits at the top
 *      when active (see service for trigger conditions).
 *
 * Performance: zero blocking work in render(). The compute is pure JS ~5ms
 * on a 24-hour input. The fetch is SWR-cached so most renders are sync-ish.
 *
 * "Add to Yawmi": we use a hash deep-link `#/yawmi/new?...` — the Yawmi
 * module either consumes the params on mount, or shows a confirmation
 * sheet. Either path emits the activity event `outdoor_window_planned`
 * via existing ActivityTracker (best-effort, fire-and-forget).
 */

import type { Widget, WidgetContext } from '../types';
import type { WeatherDetails } from '../types';
import { WeatherService } from '../../../services/WeatherService';
import { getLocationService } from '../../../services/LocationService';
import { computeOutdoorIntel } from '../../../services/PrayerWeatherIntelligence';
import type { OutdoorIntel, OutdoorWindow } from '../../../services/PrayerWeatherIntelligence';

const KIND_EMOJI: Record<OutdoorWindow['kind'], string> = {
  walk: '🚶',
  stroller: '👶',
  run: '🏃',
};
const KIND_LABEL_KEY: Record<OutdoorWindow['kind'], string> = {
  walk: 'outdoor_window_walk',
  stroller: 'outdoor_window_stroller',
  run: 'outdoor_window_run',
};

function fmtRange(startIso: string, endIso: string, locale: string): string {
  try {
    const s = new Date(startIso);
    const e = new Date(endIso);
    const fmt = (d: Date): string => d.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });
    return `${fmt(s)} – ${fmt(e)}`;
  } catch {
    return `${startIso.slice(11, 16)} – ${endIso.slice(11, 16)}`;
  }
}
function emojiForWeatherCode(code: number | undefined, rainPct: number): string {
  if (rainPct >= 60) return '🌧';
  if (rainPct >= 30) return '🌦';
  if (code === undefined) return '☁';
  if (code === 0) return '☀';
  if (code <= 3) return '🌤';
  if (code <= 48) return '🌫';
  if (code <= 67) return '🌧';
  if (code <= 77) return '❄';
  if (code <= 82) return '🌧';
  if (code >= 95) return '⛈';
  return '☁';
}

export const OutdoorWindows: Widget = {
  id: 'outdoor-windows',
  label: {
    en: 'Outdoor windows',
    fr: 'Fenêtres extérieures',
    ar: 'فُرص الخروج',
    es: 'Ventanas al aire libre',
    de: 'Outdoor-Zeitfenster',
  },
  icon: '🌤',
  defaultVisible: true,
  defaultOrder: 29,
  defaultSize: 'compact',
  visibilityRule: () => {
    // Render only when we have coordinates — otherwise the engine has no
    // weather to score and the card would be empty.
    try {
      const raw = localStorage.getItem('aweni_location');
      if (!raw) return false;
      const s = JSON.parse(raw) as { lat?: number | null };
      return typeof s.lat === 'number';
    } catch { return false; }
  },

  render(container: HTMLElement, size: 'compact' | 'expanded', ctx: WidgetContext): void {
    void size;
    const t = ctx.t;
    const loc = ctx.getLocationService();

    container.innerHTML = `
      <div data-ow-shell style="background:linear-gradient(135deg, var(--ln-bg-card,var(--card-bg,#fff)), var(--ln-bg-card,var(--card-bg,#fff)));border:1px solid var(--ln-border,var(--border,rgba(0,0,0,0.06)));border-radius:16px;padding:14px;">
        <div style="display:flex;align-items:center;gap:8px;margin-bottom:10px;">
          <span style="font-size:18px;">🌤</span>
          <span style="font-size:13px;font-weight:700;">${escapeHtml(t('widget_outdoor_title') || 'Outdoor windows')}</span>
          <span style="margin-inline-start:auto;font-size:11px;color:var(--ln-text-secondary,#6b6657);">${escapeHtml(loc.city || '')}</span>
        </div>
        <div data-ow-body>
          <div style="height:48px;background:var(--ln-bg-secondary,rgba(0,0,0,0.03));border-radius:10px;" class="skeleton-pulse"></div>
        </div>
      </div>
    `;

    const body = container.querySelector('[data-ow-body]') as HTMLElement | null;
    if (!body || loc.lat === null || loc.lng === null) return;

    // Fetch + compute on idle so we never block first paint.
    const work = (): void => {
      try {
        const ws = new WeatherService(getLocationService());
        // Try sync peek first (cache hit) — most users will land here.
        let details: WeatherDetails | null = ws.peekDetails(loc.lat as number, loc.lng as number);
        const intel = computeOutdoorIntel(details, loc);
        if (intel.windows.length > 0 || intel.hydrationNudge.fire) {
          paintIntel(intel);
        }
        // Always revalidate in background.
        ws.fetchDetails(loc.lat as number, loc.lng as number).then((fresh) => {
          if (fresh) {
            details = fresh;
            const fresher = computeOutdoorIntel(details, loc);
            paintIntel(fresher);
          }
        }).catch(() => { /* silent */ });
      } catch (err) {
        if (body) body.innerHTML = `<div style="font-size:12px;color:var(--ln-text-secondary,#6b6657);">${escapeHtml(t('widget_outdoor_offline') || 'Will compute when forecast is available.')}</div>`;
        void err;
      }
    };

    if (typeof requestIdleCallback === 'function') {
      requestIdleCallback(() => work(), { timeout: 1500 });
    } else {
      setTimeout(() => work(), 32);
    }

    function paintIntel(intel: OutdoorIntel): void {
      if (!body) return;
      // No actionable data → render a friendly indoor state once.
      if (intel.windows.length === 0 && !intel.hydrationNudge.fire) {
        body.innerHTML = `
          <div style="font-size:12px;color:var(--ln-text-secondary,#6b6657);padding:8px 0;">
            ☁ ${escapeHtml(t('widget_outdoor_indoor_day') || 'Indoor day — rain or harsh weather; no walks recommended.')}
          </div>
        `;
        return;
      }

      const cards = intel.windows.map((w) => {
        const emoji = KIND_EMOJI[w.kind];
        const titleKey = KIND_LABEL_KEY[w.kind];
        const title = t(titleKey) || titleKey;
        const range = fmtRange(w.startIso, w.endIso, ctx.locale);
        const wxEmoji = emojiForWeatherCode(undefined, w.rainPct);
        const yawmiUrl = `#/yawmi/new?title=${encodeURIComponent(title)}&start=${encodeURIComponent(w.startIso)}&end=${encodeURIComponent(w.endIso)}&kind=outdoor`;
        return `
          <div style="padding:10px;border-radius:10px;border:1px solid var(--ln-border,rgba(0,0,0,0.06));margin-bottom:6px;">
            <div style="display:flex;align-items:center;gap:8px;">
              <span style="font-size:18px;">${emoji}</span>
              <div style="flex:1;min-width:0;">
                <div style="font-size:13px;font-weight:700;">${escapeHtml(title)}</div>
                <div style="font-size:11px;color:var(--ln-text-secondary,#6b6657);">${escapeHtml(range)} · ${wxEmoji} ${w.meanTemp}°C · UV ${w.meanUv}</div>
              </div>
              <a href="${escapeAttr(yawmiUrl)}" data-ow-yawmi="${w.kind}"
                style="flex-shrink:0;padding:6px 10px;border-radius:10px;background:var(--ln-accent,#0A0A0A);color:var(--ln-accent-text,#fff);font-size:11px;font-weight:700;text-decoration:none;min-height:36px;display:inline-flex;align-items:center;">
                + ${escapeHtml(t('widget_outdoor_add_yawmi') || 'Add to Yawmi')}
              </a>
            </div>
          </div>
        `;
      }).join('');

      const hydration = intel.hydrationNudge.fire ? `
        <div style="padding:10px;border-radius:10px;background:#f59e0b15;border:1px solid #f59e0b40;margin-bottom:6px;display:flex;align-items:center;gap:8px;">
          <span style="font-size:18px;">💧</span>
          <div style="flex:1;font-size:12px;color:#92400e;">
            ${escapeHtml(
              (t('widget_outdoor_hydration') || 'Hydrate before {prayer} — UV {uv}, expected {temp}°C')
                .replace('{prayer}', t('prayer_' + (intel.hydrationNudge.before || 'dhuhr')) || (intel.hydrationNudge.before || 'dhuhr'))
                .replace('{uv}', String(intel.hydrationNudge.uvPeak.toFixed(0)))
                .replace('{temp}', String(intel.hydrationNudge.tempPeak.toFixed(0)))
            )}
          </div>
        </div>
      ` : '';

      body.innerHTML = hydration + cards;

      // Wire activity event on Yawmi CTA (best-effort).
      body.querySelectorAll<HTMLAnchorElement>('[data-ow-yawmi]').forEach((a) => {
        a.addEventListener('click', () => {
          const kind = a.dataset.owYawmi || 'walk';
          // Best-effort activity event: navigate happens via the <a> href so
          // even if the activity POST fails, the user reaches Yawmi.
          fetch('/aweni/api/v1/activity', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ type: 'outdoor_window_planned', kind, source: 'outdoor-windows' }),
            keepalive: true,
            credentials: 'include',
          }).catch(() => { /* silent */ });
        });
      });
    }
  },
};

function escapeHtml(s: string): string {
  return String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}
function escapeAttr(s: string): string {
  return String(s).replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;');
}
